import { useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'

import { cn } from '@modules/lib/lib'
import { Input } from '@modules/shadcn/ui/Input/Input'

import {
  countHistoryTitleLength,
  HISTORY_TITLE_MAX_LENGTH,
  type ResolvedHistoryTitle,
} from '../utils/historyDisplay'
import { isImeComposingKeyEvent } from '../utils/isImeComposingKeyEvent'

interface HistoryTitleEditorProps {
  /** サーバーが返すタイトル（未設定は空文字列） */
  title: string
  /** 表示用（タイトル、または未設定時の既定表示） */
  resolved: ResolvedHistoryTitle
  /** 保存。空文字列は未設定に戻す。失敗したら例外を投げる（呼び出し側が通知する） */
  onSave?: (title: string) => Promise<void> | void
  /** 入力欄のid（複数カードで一意にする） */
  inputId: string
  className?: string
}

/**
 * 企業画面の履歴タイトル（表示＋その場のインライン編集。モックの状態5）。
 *
 * - 鉛筆ボタンで入力欄に変わる。Enterで保存・Escで取消・フォーカスを外しても保存（日本語変換を確定するEnterは除く）
 * - 30文字まで。空で保存すると未設定に戻る（サーバーがNULLにする）
 * - 未設定は細字・薄い色で既定表示（「日時 氏名」）を出し、「タイトル未設定」を添える
 */
export const HistoryTitleEditor: React.FC<HistoryTitleEditorProps> = ({
  title,
  resolved,
  onSave,
  inputId,
  className,
}) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(title)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Escで取り消したあとのblurで保存しないための印
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (!editing) setValue(title)
  }, [title, editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const commit = async () => {
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }
    const next = value.trim()
    setEditing(false)
    if (next === title.trim() || !onSave) return
    setSaving(true)
    try {
      await onSave(next)
    } catch (error) {
      console.error('[HistoryTitleEditor] タイトルの保存に失敗:', error)
      setValue(title)
    } finally {
      setSaving(false)
    }
  }

  const length = countHistoryTitleLength(value)
  const tooLong = length > HISTORY_TITLE_MAX_LENGTH

  if (editing) {
    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <label className="sr-only" htmlFor={inputId}>
          タイトル
        </label>
        <Input
          aria-invalid={tooLong}
          className="h-7 px-2 text-xs"
          id={inputId}
          onBlur={() => void commit()}
          onChange={event => setValue(event.target.value)}
          onKeyDown={event => {
            // 日本語変換を確定するEnterでは保存しない（確定後にもう一度Enterで保存）
            if (isImeComposingKeyEvent(event)) return
            if (event.key === 'Enter' && !tooLong) {
              event.preventDefault()
              inputRef.current?.blur()
            } else if (event.key === 'Escape') {
              event.preventDefault()
              cancelledRef.current = true
              setValue(title)
              setEditing(false)
            }
          }}
          ref={inputRef}
          value={value}
        />
        <p className={cn('text-[10px] text-muted-foreground', tooLong && 'text-destructive')}>
          Enterで保存・Escで取消 {length}/{HISTORY_TITLE_MAX_LENGTH}
        </p>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <div className="flex items-center gap-1">
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-xs',
            resolved.isDefault ? 'font-normal text-muted-foreground' : 'font-semibold',
          )}
          title={resolved.text}
        >
          {resolved.text}
        </span>
        {onSave ? (
          <button
            aria-label={resolved.isDefault ? 'タイトルを付ける' : 'タイトルを編集'}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            disabled={saving}
            onClick={event => {
              event.stopPropagation()
              setEditing(true)
            }}
            type="button"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      {resolved.isDefault ? (
        <p className="text-[10px] text-muted-foreground/70">タイトル未設定</p>
      ) : null}
    </div>
  )
}
